import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usersService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Plus, MoreVertical, RefreshCw, Users, Building2, Database } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const departments = [
  "Social Media Ambassadors",
  "HR",
  "Email Marketing",
  "Client Relations",
  "Creativity",
  "Content Creation",
  "Public Relations",
  "Digital Marketing",
  "Executive"
];

const roles = ["COO", "CEO", "Overall Marketing Team Lead", "Team Lead", "Assistant Team Lead", "Team Lead/ HR Manager", "HR Manager", "Team Member", "HR Intern"];

const seedUsers = [
  // Social Media Ambassadors Department
  { name: "Favour Nwabude", email: "favour@havanagroups.com", role: "Team Lead", department: "Social Media Ambassadors", permissions: ["view", "create", "edit"] },
  { name: "Sesughter Terna", email: "Sesughter@havanagroups.com", role: "Assistant Team Lead", department: "Social Media Ambassadors", permissions: ["view", "create"] },
  { name: "Grace Musa", email: "Grace@havanagroups.com", role: "Team Member", department: "Social Media Ambassadors", permissions: ["view"] },
  { name: "Chisom Onu", email: "chisom@havanagroups.com", role: "Team Member", department: "Social Media Ambassadors", permissions: ["view"] },
  // HR Department
  { name: "Tochi Onah", email: "Tochi@havanagroups.com", role: "HR Intern", department: "HR", permissions: ["view"] },
  { name: "Joy Mbah", email: "hr@havanagroups.com", role: "Team Lead/ HR Manager", department: "HR", permissions: ["view", "create", "edit"] },
  // Email Marketing Department
  { name: "Daniel Ifeanyi", email: "ifeanyi@havanagroups.com", role: "Team Lead", department: "Email Marketing", permissions: ["view", "create", "edit"] },
  { name: "Kalu Chiemeka", email: "kalu@havanagroups.com", role: "Team Member", department: "Email Marketing", permissions: ["view"] },
  // Client Relations Department
  { name: "Precious Okeke", email: "precious@havanagroups.com", role: "Team Member", department: "Client Relations", permissions: ["view"] },
  { name: "Amara Blessing", email: "blessing@havanagroups.com", role: "Team Member", department: "Client Relations", permissions: ["view"] },
  { name: "Naomi Mbah", email: "naomi@havanagroups.com", role: "Team Lead", department: "Client Relations", permissions: ["view", "create", "edit"] },
  { name: "David Martins", email: "Martins@havanagroups.com", role: "Team Member", department: "Client Relations", permissions: ["view"] },
  { name: "Kamsi Chiedozie", email: "kamsi@havanagroups.com", role: "Team Member", department: "Client Relations", permissions: ["view"] },
  // Creativity Department
  { name: "Kingsley Vincent", email: "kingsley@havanagroups.com", role: "Team Lead", department: "Creativity", permissions: ["view", "create", "edit"] },
  { name: "Cheta Onyinanya", email: "cheta@havanagroups.com", role: "Team Member", department: "Creativity", permissions: ["view"] },
  { name: "Emmanuel Emehel", email: "emmanuel@havanagroups.com", role: "Team Member", department: "Creativity", permissions: ["view"] },
  // Content Creation Department
  { name: "Christabella Aniugwu", email: "Christabella@havanagroups.com", role: "Team Lead", department: "Content Creation", permissions: ["view", "create", "edit"] },
  { name: "Ogechukwu Maduka", email: "Ogechukwu@havanagroups.com", role: "Team Member", department: "Content Creation", permissions: ["view"] },
  // Public Relations Department
  { name: "Amaechina Chinazaekpere", email: "chinazaekpere@havanagroups.com", role: "Team Member", department: "Public Relations", permissions: ["view"] },
  { name: "Asuquo Aniefiok", email: "Aniefiok@havanagroups.com", role: "Team Member", department: "Public Relations", permissions: ["view"] },
  { name: "Uche Ihuoma", email: "ihuoma@havanagroups.com", role: "Team Member", department: "Public Relations", permissions: ["view"] },
  { name: "Eneh Chidera", email: "chidera@havanagroups.com", role: "Team Member", department: "Public Relations", permissions: ["view"] },
  { name: "Somto Juddie", email: "juddie@havanagroups.com", role: "Team Lead", department: "Public Relations", permissions: ["view", "create", "edit"] },
  { name: "Bethel", email: "bethel@havanagroups.com", role: "Team Member", department: "Public Relations", permissions: ["view"] },
  // Digital Marketing Manager
  { name: "Henry Kings", email: "henry@havanagroups.com", role: "Overall Marketing Team Lead", department: "Digital Marketing", permissions: ["view", "create", "edit", "approve"] },
  // Chief Operating Officer
  { name: "Daniel Chinedu", email: "daniel@havanagroups.com", role: "COO", department: "Executive", permissions: ["all"] },
];

const Team = () => {
  const { userData, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    role: "Team Member",
    department: "Social Media Ambassadors"
  });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await usersService.getAll();
        setUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [userData]);

  const refreshUsers = async () => {
    setLoading(true);
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === "all" || user.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  // Group users by department for the grouped view
  const groupedUsers = {};
  filteredUsers.forEach((user) => {
    const dept = user.department || "Unassigned";
    if (!groupedUsers[dept]) groupedUsers[dept] = [];
    groupedUsers[dept].push(user);
  });

  // Sort each department: leads first, then alphabetically
  const roleOrder = {
    "COO": 0,
    "Overall Marketing Team Lead": 1,
    "Team Lead/ HR Manager": 2,
    "Team Lead": 3,
    "Assistant Team Lead": 4,
    "HR Manager": 5,
    "Team Member": 6,
    "HR Intern": 7
  };

  Object.keys(groupedUsers).forEach((dept) => {
    groupedUsers[dept].sort((a, b) => {
      const orderA = roleOrder[a.role] ?? 99;
      const orderB = roleOrder[b.role] ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || "").localeCompare(b.name || "");
    });
  });

  // Department display order
  const deptDisplayOrder = [
    "Executive",
    "Digital Marketing",
    "Social Media Ambassadors",
    "HR",
    "Email Marketing",
    "Client Relations",
    "Creativity",
    "Content Creation",
    "Public Relations"
  ];

  const sortedDepartments = Object.keys(groupedUsers).sort((a, b) => {
    const idxA = deptDisplayOrder.indexOf(a);
    const idxB = deptDisplayOrder.indexOf(b);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await usersService.create(newMember);
      toast.success("Team member added successfully");
      setShowAddModal(false);
      setNewMember({ name: "", email: "", role: "Team Member", department: "Social Media Ambassadors" });
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error("Failed to add team member");
    } finally {
      setSaving(false);
    }
  };

  const handleSeedUsers = async () => {
    setSeeding(true);
    try {
      // Get currently existing users to avoid duplicates
      const existingUsers = await usersService.getAll();
      const existingEmails = new Set(existingUsers.map(u => u.email?.toLowerCase()));
      
      const usersToCreate = seedUsers.filter(u => !existingEmails.has(u.email?.toLowerCase()));
      
      if (usersToCreate.length === 0) {
        toast.info("All seed users already exist in the database");
        return;
      }

      console.log(`Starting to seed ${usersToCreate.length} new users...`);
      for (const user of usersToCreate) {
        await usersService.create(user);
      }
      
      toast.success(`${usersToCreate.length} team members seeded successfully`);
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Error seeding users:", error);
      toast.error("Failed to seed users: " + error.message);
    } finally {
      setSeeding(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (index) => {
    const colors = [
      "bg-gradient-to-br from-blue-500 to-blue-600",
      "bg-gradient-to-br from-purple-500 to-purple-600",
      "bg-gradient-to-br from-pink-500 to-pink-600",
      "bg-gradient-to-br from-green-500 to-green-600",
      "bg-gradient-to-br from-amber-500 to-amber-600",
      "bg-gradient-to-br from-red-500 to-red-600",
      "bg-gradient-to-br from-teal-500 to-teal-600",
      "bg-gradient-to-br from-indigo-500 to-indigo-600",
      "bg-gradient-to-br from-cyan-500 to-cyan-600"
    ];
    return colors[index % colors.length];
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      "COO": "bg-red-100 text-red-700",
      "Overall Marketing Team Lead": "bg-purple-100 text-purple-700",
      "Marketing Team Lead": "bg-purple-100 text-purple-700",
      "Team Lead": "bg-blue-100 text-blue-700",
      "Team Lead/ HR Manager": "bg-emerald-100 text-emerald-700",
      "Assistant Team Lead": "bg-amber-100 text-amber-700",
      "HR Manager": "bg-green-100 text-green-700",
      "Team Member": "bg-gray-100 text-gray-700",
      "HR Intern": "bg-cyan-100 text-cyan-700"
    };
    return roleColors[role] || "bg-gray-100 text-gray-700";
  };

  const getDeptIcon = (dept) => {
    const icons = {
      "Executive": "👔",
      "Digital Marketing": "📊",
      "Social Media Ambassadors": "📱",
      "HR": "🤝",
      "Email Marketing": "📧",
      "Client Relations": "💼",
      "Creativity": "🎨",
      "Content Creation": "✍️",
      "Public Relations": "📢"
    };
    return icons[dept] || "🏢";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-action-blue" />
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy">Team</h1>
          <p className="text-gray-500">Manage your team members and their roles</p>
        </div>
        <div className="flex items-center gap-2">
          {(userData?.role === "COO" || userData?.role?.includes("Team Lead")) && (
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowAddModal(true)} className="bg-navy hover:bg-navy-light text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
              {userData?.role === "COO" && (
                <Button 
                  onClick={handleSeedUsers} 
                  variant="outline" 
                  disabled={seeding}
                  className="border-navy text-navy hover:bg-navy/5"
                >
                  {seeding ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4 mr-2" />
                  )}
                  Seed Team
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" size="icon" onClick={refreshUsers} title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-t-4 border-t-action-blue">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-action-blue" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{filteredUsers.length}</p>
                <p className="text-xs text-gray-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-green-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                <Building2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{sortedDepartments.length}</p>
                <p className="text-xs text-gray-500">Departments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-purple-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center">
                <span className="text-lg">⭐</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {filteredUsers.filter(u => u.role?.includes("Lead") || u.role === "COO" || u.role?.includes("Manager")).length}
                </p>
                <p className="text-xs text-gray-500">Team Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-amber-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center">
                <span className="text-lg">👥</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {filteredUsers.filter(u => u.role === "Team Member" || u.role === "HR Intern").length}
                </p>
                <p className="text-xs text-gray-500">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
            >
              <option value="all">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Team grid */}
      {sortedDepartments.map((dept) => (
        <div key={dept} className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">{getDeptIcon(dept)}</span>
            <div>
              <h2 className="text-lg font-semibold text-navy">{dept} Department</h2>
              <p className="text-sm text-gray-500">{groupedUsers[dept].length} member{groupedUsers[dept].length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupedUsers[dept].map((user, index) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-semibold text-lg ${getAvatarColor(index)}`}>
                      {getInitials(user.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-navy truncate">{user.name}</h3>
                          <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <Badge className={getRoleBadge(user.role)}>
                          {user.role}
                        </Badge>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full" />
                            <span className="text-gray-500">Available</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {filteredUsers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">No team members found</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <Input
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <Input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={newMember.role}
                onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              >
                {roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                value={newMember.department}
                onChange={(e) => setNewMember({ ...newMember, department: e.target.value })}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              >
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddMember} disabled={saving} className="bg-white text-black border border-gray-300 hover:bg-gray-100">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;